import "styles/tabSpecRun.css"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Modal, Progress } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import type { QueryQueue } from "features/Dashboard/runtime/queryQueue"
import { collectBook } from "../../export/tabBook"
import { runBook } from "../../runtime/runTable"
import { useTabSpecStore, useTabSpecStoreApi } from "../../runtime/tabSpecStore"
import { runAvailability } from "./runAvailability"

export interface RunDialogProps {
  open: boolean
  onClose: () => void
  /** The session's query queue; nothing runs without it. */
  queue: QueryQueue | null
  /** After "Show table" has selected the table and closed the dialog. */
  onShowTable: (tableId: string) => void
  /** After "Download Excel" has closed the dialog: opens the download dialog. */
  onDownload: () => void
}

const summaryText = (ready: number, total: number): string => {
  if (ready < total) return `${ready} of ${total} tables are ready.`
  return total === 1 ? "The table is ready." : `All ${total} tables are ready.`
}

/**
 * Runs the tab book and shows how far it has got. Opening the dialog starts
 * the book; only one book runs at a time, so a dialog opened while a book
 * runs (it was closed and opened again) starts nothing and shows that book.
 * Closing the dialog does not stop the book; "Stop" does, once the table
 * that is running has finished. At the end it lists the tables that failed
 * or were skipped, each with a way to it.
 */
const RunDialog: React.FC<RunDialogProps> = ({
  open,
  onClose,
  queue,
  onShowTable,
  onDownload,
}) => {
  const store = useTabSpecStoreApi()
  const bookRun = useTabSpecStore((s) => s.bookRun)
  const cancelBook = useTabSpecStore((s) => s.cancelBook)
  const select = useTabSpecStore((s) => s.select)
  const blocked = useTabSpecStore((s) => runAvailability(s).reason)
  // What the list of failed and skipped tables is read from.
  const doc = useTabSpecStore((s) => s.document)
  const runs = useTabSpecStore((s) => s.runs)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const session = useTabSpecStore((s) => s.session)
  /** Whether this opening started a book or found one running: the dialog shows `bookRun` only then. */
  const [watching, setWatching] = useState(false)
  const started = useRef(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const dialogContainer = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  const start = useCallback(
    (q: QueryQueue): void => {
      setWatching(true)
      // One book at a time: the one that runs is the one shown.
      if (store.getState().bookRun?.active) return
      runBook(store, q).catch((e: unknown) =>
        notify.error(
          "Tab book",
          e instanceof Error && e.message ? e.message : "The tab book could not be run."
        )
      )
    },
    [store]
  )

  // Once per opening: show the book that runs, or start one when the spec
  // can run. A queue or a session that arrives while the dialog is open
  // starts it then.
  useEffect(() => {
    if (!open) {
      started.current = false
      setWatching(false)
      return
    }
    if (started.current) return
    const state = store.getState()
    if (state.bookRun?.active) {
      started.current = true
      setWatching(true)
      return
    }
    if (!queue || !runAvailability(state).canRun) return
    started.current = true
    start(queue)
  }, [open, queue, blocked, store, start])

  const shown = watching ? bookRun : null
  const finished = shown !== null && !shown.active
  const issues = useMemo(
    () =>
      finished
        ? collectBook({ ...store.getState(), document: doc, runs, catalogue, session }).filter(
            (t) => t.status === "error" || t.status === "skipped"
          )
        : [],
    [finished, store, doc, runs, catalogue, session]
  )

  const showTable = (tableId: string): void => {
    select(tableId)
    onClose()
    onShowTable(tableId)
  }
  const download = (): void => {
    onClose()
    onDownload()
  }

  // The platform's footer: Close (the way out) first, the main action last.
  const close = (
    <DataQButton key="close" type="secondary" onClick={onClose}>
      Close
    </DataQButton>
  )
  const footer = !shown
    ? [close]
    : shown.active
      ? [
          close,
          <DataQButton key="stop" type="danger" isDisabled={shown.cancelled} onClick={cancelBook}>
            Stop
          </DataQButton>,
        ]
      : [
          close,
          ...(queue && !blocked
            ? [
                <DataQButton key="again" type="secondary" onClick={() => start(queue)}>
                  Run again
                </DataQButton>,
              ]
            : []),
          <DataQButton key="download" type="primary" onClick={download}>
            Download Excel
          </DataQButton>,
        ]

  let body: React.ReactNode
  if (!shown) {
    const reason = blocked ?? (queue ? null : "The data session is not ready.")
    body = reason ? (
      <Alert type="info" showIcon message={reason} />
    ) : (
      <p className="ts-run__note">Starting...</p>
    )
  } else {
    const { total, done, failed, skipped, cancelled, active } = shown
    body = (
      <>
        <Progress
          aria-label="Tables run"
          percent={total > 0 ? Math.round((done / total) * 100) : 100}
          format={() => `${done} of ${total}`}
          status={active ? "active" : "normal"}
        />
        {(failed > 0 || skipped > 0) && (
          <div className="ts-run__counts">
            {failed > 0 && <span className="ts-run__failed">{failed} failed</span>}
            {skipped > 0 && <span>{skipped} skipped</span>}
          </div>
        )}
        {active && cancelled && (
          <p className="ts-run__note">The table that is running will finish first.</p>
        )}
        {!active && (
          <>
            <p className="ts-run__summary">
              {summaryText(done - failed - skipped, total)}
            </p>
            {cancelled && done < total && (
              <p className="ts-run__note">The run was stopped before every table had run.</p>
            )}
            {shown.reason && <Alert type="warning" showIcon message={shown.reason} />}
            {issues.length > 0 && (
              <ul className="ts-run__issues" aria-label="Tables that are not ready">
                {issues.map((t) => (
                  <li key={t.tableId} className="ts-run__issue">
                    <div className="ts-run__issue-text">
                      <span className="ts-run__issue-title">
                        {`Table ${t.number}: ${t.title}`}
                      </span>
                      <span className="ts-run__issue-message">
                        {`${t.status === "error" ? "Failed" : "Skipped"}: ${t.message ?? ""}`}
                      </span>
                    </div>
                    <Button
                      size="small"
                      type="link"
                      className="ts-link"
                      onClick={() => showTable(t.tableId)}
                    >
                      Show table
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <>
      <span ref={anchorRef} hidden />
      <Modal
        open={open}
        title="Run tab book"
        onCancel={onClose}
        footer={footer}
        getContainer={dialogContainer}
        width={560}
      >
        <div className="ts-run">{body}</div>
      </Modal>
    </>
  )
}

export default RunDialog
